import React, { useEffect } from 'react';
import { Colors } from 'mm-theme-configuration';
import { css } from 'aphrodite/no-important';
import { ErrorBoundary } from '../../errorHandling/ErrorBoundary';
import { H3 } from '../../../typography/semanticTags';
import { CardComponentDataProps } from '../../partials/cards/cards.utils';
import { useTheme } from '../../../theming/useTheme';
import { createStyles } from './suggestedPosts.styles';
import {
  componentConfigurationKey as SectionHeaderConfigThemeKey,
  SectionHeader,
  SectionHeaderComponentThemeProps,
} from '../../wrappers/sectionHeader/SectionHeader';
import { useThemeOverride } from '../../../theming/useThemeOverride';
import { ThemeContext } from '../../../theming/ThemeProviderWithFonts';
import { SuggestedPostsWrapper, SuggestedPostsWrapperProps } from './SuggestedPostsWrapper';
import { ArticleLineNew } from './articleLine/ArticleLineNew';
import { ArticleLine } from './articleLine/ArticleLine';

const LOOK_AND_FEEL_TEST_FS_ID = 'recirculation-v1-look-n-feel-fs|designv2';
const LOOK_AND_FEEL_TEST_VOLTAX_ID = 'recirculation-v1-look-n-feel-voltax|designv2';

interface SuggestedPostsProps {
    articles: Array<CardComponentDataProps>;
    experiment?: string;
}

export type SuggestedPostsPropsType = SuggestedPostsProps & SuggestedPostsWrapperProps

export interface AnalyticsEventParams {
  action: string;
  category: string;
  label: string;
}

const getAnalyticsEventParams = (numberOfLinks: number, linkNumber: number, linkUrl: string, total_blocks: number): AnalyticsEventParams => ({
  category: 'Article',
  action: 'semantic_recommendation_links_click',
  label: `after_block=[3];total_blocks=[${total_blocks}];number_of_links=[${numberOfLinks}];clicked_link_number=[${linkNumber}];clicked_link_url=[${linkUrl}]`,
});

export const SuggestedPosts = (props: SuggestedPostsPropsType): JSX.Element => {
  const { articles, defaultBlocksWidths, dataId, total_blocks, experiment } = props;

  const numberOfLinks = articles ? articles.length : 0;
  const isRecirculationTest = [LOOK_AND_FEEL_TEST_FS_ID, LOOK_AND_FEEL_TEST_VOLTAX_ID].some(testId => experiment?.includes(testId));

  useEffect(() => {
    window.mmClientApi = window.mmClientApi || [];
    window.mmClientApi.push('analytics', {
      event_category: 'Article',
      event_action: 'semantic_recommendation_links_embed',
      event_label: `after_block=[3];total_blocks=[${total_blocks}];number_of_links=[${numberOfLinks}]`,
    });
  }, [total_blocks, numberOfLinks]);

  const suggestedPostsAnalytics = (linkNumber: number, linkUrl: string) => {
    const analyticsEventParams = getAnalyticsEventParams(numberOfLinks, linkNumber, linkUrl, total_blocks);
    return () => {
      window.mmClientApi = window.mmClientApi || [];
      window.mmClientApi.push('analytics', {
        event_category: analyticsEventParams.category,
        event_action: analyticsEventParams.action,
        event_label: analyticsEventParams.label,
      });
    };
  };

  const { colors }: { colors: Colors } = useTheme();
  const styles = createStyles(colors, isRecirculationTest);
  const title = isRecirculationTest ? 'Editor\'s Picks' : 'More from Our Site';

  const paddingFactors = { large: 0, medium: 0, small: 0 };

  const themeContextSectionHeader = useThemeOverride<SectionHeaderComponentThemeProps>({
    middleHorizontalLineColor: colors.primary,
    showMiddleHorizontalLine: true,
    showStrikeThrowHorizontalLine: false,
    showSquare: false,
    showBorderTop: false,
    showBorderBottom: false,
    titleColor: colors.black,
    titleBackgroundColor: 'transparent',
    largeVerticalGap: 0.5,
    mediumVerticalGap: 0.5,
    smallVerticalGap: 0.5,
    largeMarginBottomFactor: 1,
    mediumMarginBottomFactor: 1,
    smallMarginBottomFactor: 1,
  }, SectionHeaderConfigThemeKey);

  const themeContextSectionHeaderTest = useThemeOverride<SectionHeaderComponentThemeProps>({
    showMiddleHorizontalLine: false,
    showStrikeThrowHorizontalLine: false,
    showSquare: false,
    showBorderTop: false,
    showBorderBottom: true,
    titleColor: colors.primary,
    titleBackgroundColor: 'transparent',
    largeMarginBottomFactor: 0,
    mediumMarginBottomFactor: 0,
    smallMarginBottomFactor: 0,
    largeHorizontalGap: 0.75,
    mediumHorizontalGap: 0.75,
    smallHorizontalGap: 0.75,
    borderBottomColor: colors.lightGrey,
    borderBottomWidth: 0.05,
  }, SectionHeaderConfigThemeKey);

  return (
    <ErrorBoundary>
      <SuggestedPostsWrapper
        defaultBlocksWidths={defaultBlocksWidths}
        dataId={dataId}
        numberOfLinks={numberOfLinks}
        total_blocks={total_blocks}
      >
        <div className={css(styles.titleWrapper)}>
          <ThemeContext.Provider value={isRecirculationTest ? themeContextSectionHeaderTest : themeContextSectionHeader}>
            <SectionHeader moreButtonText={null} moreButtonSvg={null} paddingFactors={paddingFactors}>
              <H3>
                {title}
              </H3>
            </SectionHeader>
          </ThemeContext.Provider>
        </div>
        <ul className={css(styles.articlesWrapper)}>
          {articles.map((article, index) => {
            const analyticsEventParams = getAnalyticsEventParams(numberOfLinks, index + 1, article.articleUrl, total_blocks);
            const articleLineProps = { key: article.id, index, article, analyticsEventParams, suggestedPostsAnalytics, styles };
            return (
              isRecirculationTest
                ? <ArticleLineNew {...articleLineProps} />
                : <ArticleLine {...articleLineProps} />
            );
          })}
        </ul>
        {!isRecirculationTest && <div className={css(styles.sectionDivider)} />}
      </SuggestedPostsWrapper>
    </ErrorBoundary>
  );
};
